import { loadGoogleMapsApi } from '@deliveroo/remote-script-loader';

/**
 * Google’s Maps API’s own geocoder uses a callback rather than a promise, which
 * somewhat reduces its utility. This wrapper chains loadGoogleMapsApi with a
 * standard Promise-creator, so that its behaviour can be more neatly
 * encapsulated and tested.
 *
 * Like Google’s Geocoder, it can accept one of three mechanisms for geocoding:
 *
 * 1. searchString (i.e. search by address string)
 * 2. placeId — string place identifier)
 * 3. coords — { lat, lng } object for reverse-geocoding.
 *
 * Only one of these mechanisms can be used at a time, and they are chosen in
 * the above order of precedence.
 *
 * See https://developers.google.com/maps/documentation/javascript/geocoding for
 * more info.
 */
export function geocodeLocation({ searchString, placeId, coords, googleApiKey }) {
  return new Promise((resolve, reject) =>
    loadGoogleMapsApi({ googleApiKey })
      .then(() => {
        const geocoder = new window.google.maps.Geocoder();
        const searchParams = (() => {
          switch (true) {
            case !!searchString:
              return { address: searchString };
            case !!placeId:
              return { placeId };
            default:
              return { location: coords };
          }
        })();

        geocoder.geocode(searchParams, (results, status) => {
          if (status === 'OK') {
            resolve(results);
          } else {
            reject(status);
          }
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Google Maps API failed to load', { error });
        reject(error);
      }),
  );
}

/**
 * Google’s Maps API’s AutocompleteService uses a callback rather than a
 * promise, which somewhat reduces its utility. This wrapper chains
 * loadGoogleMapsApi with a standard Promise-creator, so that its behaviour can
 * be more neatly encapsulated and tested.
 */
export function getPlacePredictions({ searchString, tld, googleApiKey }) {
  return new Promise((resolve, reject) =>
    loadGoogleMapsApi({ googleApiKey, libraries: ['places'] })
      .then(() => {
        const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
        const autocompleteService = new window.google.maps.places.AutocompleteService();

        autocompleteService.getPlacePredictions(
          {
            input: searchString,
            componentRestrictions: { country: tld },
            types: ['geocode'],
            sessionToken,
          },
          (suggestions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(suggestions);
            } else {
              reject(status);
            }
          },
        );
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Google Maps API failed to load', { error });
        reject(error);
      }),
  );
}
